import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export const useSlides = (projectId) => {
  const [slides, setSlides] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (projectId) {
      loadSlides()
    }
  }, [projectId])

  const loadSlides = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('slides')
        .select(`
          *,
          elements(*)
        `)
        .eq('project_id', projectId)
        .order('slide_order')

      if (error) throw error

      console.log('🔍 Loaded slides from database with order:', data?.map(slide => `${slide.slide_order}: ${slide.id}`) || [])
      setSlides(data || [])
    } catch (err) {
      console.error('Error loading slides:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createSlide = async (slideData) => {
    try {
      const maxOrder = Math.max(...slides.map(s => s.slide_order), 0)
      
      const { data, error } = await supabase
        .from('slides')
        .insert([{
          project_id: projectId,
          slide_order: maxOrder + 1,
          ...slideData
        }])
        .select()
        .single()

      if (error) throw error

      await loadSlides()
      return { data, error: null }
    } catch (err) {
      console.error('Error creating slide:', err)
      return { data: null, error: err }
    }
  }

  const updateSlide = async (slideId, updates) => {
    try {
      const { data, error } = await supabase
        .from('slides')
        .update(updates)
        .eq('id', slideId)
        .select()
        .single()

      if (error) throw error

      await loadSlides()
      return { data, error: null }
    } catch (err) {
      console.error('Error updating slide:', err)
      return { data: null, error: err }
    }
  }

  const deleteSlide = async (slideId) => {
    try {
      const { error } = await supabase
        .from('slides')
        .delete()
        .eq('id', slideId)

      if (error) throw error

      await loadSlides()
      return { error: null }
    } catch (err) {
      console.error('Error deleting slide:', err)
      return { error: err }
    }
  }

  const reorderSlides = async (slideId, newOrder) => {
    try {
      const { error } = await supabase
        .from('slides')
        .update({ slide_order: newOrder })
        .eq('id', slideId)

      if (error) throw error

      await loadSlides()
      return { error: null }
    } catch (err) {
      console.error('Error reordering slides:', err)
      return { error: err }
    }
  }

  const createElement = async (slideId, elementData) => {
    try {
      const { data, error } = await supabase
        .from('elements')
        .insert([{
          slide_id: slideId,
          ...elementData
        }])
        .select()
        .single()

      if (error) throw error

      await loadSlides()
      return { data, error: null }
    } catch (err) {
      console.error('Error creating element:', err)
      return { data: null, error: err }
    }
  }

  const updateElement = async (elementId, updates) => {
    try {
      const { data, error } = await supabase
        .from('elements')
        .update(updates)
        .eq('id', elementId)
        .select()
        .single()

      if (error) throw error

      await loadSlides()
      return { data, error: null }
    } catch (err) {
      console.error('Error updating element:', err)
      return { data: null, error: err }
    }
  }

  const deleteElement = async (elementId) => {
    try {
      const { error } = await supabase
        .from('elements')
        .delete()
        .eq('id', elementId)

      if (error) throw error

      await loadSlides()
      return { error: null }
    } catch (err) {
      console.error('Error deleting element:', err)
      return { error: err }
    }
  }

  return {
    slides,
    loading,
    error,
    loadSlides,
    createSlide,
    updateSlide,
    deleteSlide,
    reorderSlides,
    createElement,
    updateElement,
    deleteElement
  }
}